import React, { useEffect, useState } from 'react';
import type { AssetCategory, AssetCondition, AssetCurrency, AssetKind, AssetLocation, AssetStatus, RestaurantAsset } from '../../types';
import { ASSET_CATEGORIES, ASSET_CONDITIONS, ASSET_CURRENCIES, ASSET_LOCATIONS, ASSET_STATUSES } from '../../lib/assets';

interface AssetModalProps {
  asset: RestaurantAsset | null;
  onClose: () => void;
  onSave: (asset: RestaurantAsset) => void;
}

function toDateInput(ms?: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromDateInput(value: string): number | undefined {
  if (!value) return undefined;
  const ms = new Date(`${value}T00:00:00`).getTime();
  return Number.isNaN(ms) ? undefined : ms;
}

const inputClass =
  'w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500';

const AssetModal: React.FC<AssetModalProps> = ({ asset, onClose, onSave }) => {
  const [kind, setKind] = useState<AssetKind>('quantity');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<AssetCategory>('kitchen_equipment');
  const [location, setLocation] = useState<AssetLocation>('kitchen');
  const [quantity, setQuantity] = useState('1');
  const [replaceBelow, setReplaceBelow] = useState('');
  const [assetTag, setAssetTag] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [unitCostCurrency, setUnitCostCurrency] = useState<AssetCurrency>('NZD');
  const [supplier, setSupplier] = useState('');
  const [condition, setCondition] = useState<AssetCondition>('new');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<AssetStatus>('in_use');

  useEffect(() => {
    if (asset) {
      setKind(asset.kind);
      setName(asset.name);
      setCategory(asset.category);
      setLocation(asset.location);
      setQuantity(String(asset.quantity ?? 1));
      setReplaceBelow(asset.replaceBelow != null ? String(asset.replaceBelow) : '');
      setAssetTag(asset.assetTag || '');
      setSerialNumber(asset.serialNumber || '');
      setPurchaseDate(toDateInput(asset.purchaseDate));
      setUnitCost(asset.unitCost != null ? String(asset.unitCost) : '');
      setUnitCostCurrency(asset.unitCostCurrency || 'NZD');
      setSupplier(asset.supplier || '');
      setCondition(asset.condition || 'new');
      setNotes(asset.notes || '');
      setStatus(asset.status);
    } else {
      setKind('quantity');
      setName('');
      setCategory('kitchen_equipment');
      setLocation('kitchen');
      setQuantity('1');
      setReplaceBelow('');
      setAssetTag('');
      setSerialNumber('');
      setPurchaseDate('');
      setUnitCost('');
      setUnitCostCurrency('NZD');
      setSupplier('');
      setCondition('new');
      setNotes('');
      setStatus('in_use');
    }
  }, [asset]);

  const handleKindChange = (next: AssetKind) => {
    setKind(next);
    if (next === 'tagged') {
      setQuantity('1');
      setReplaceBelow('');
    }
  };

  const blockEnterSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      alert('Please enter a name.');
      return;
    }
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty < 0) {
      alert('Quantity must be zero or more.');
      return;
    }
    const replace = replaceBelow.trim() === '' ? undefined : Number(replaceBelow);
    if (replace != null && (!Number.isFinite(replace) || replace < 0)) {
      alert('Replace-below must be zero or more.');
      return;
    }
    const cost = unitCost.trim() === '' ? undefined : Number(unitCost);
    if (cost != null && (!Number.isFinite(cost) || cost < 0)) {
      alert('Unit cost must be zero or more.');
      return;
    }
    const now = Date.now();
    onSave({
      id: asset?.id || '',
      kind,
      name: trimmed,
      category,
      location,
      quantity: kind === 'tagged' ? 1 : qty,
      replaceBelow: kind === 'quantity' ? replace : undefined,
      assetTag: kind === 'tagged' ? assetTag.trim() || undefined : undefined,
      serialNumber: kind === 'tagged' ? serialNumber.trim() || undefined : undefined,
      purchaseDate: fromDateInput(purchaseDate),
      unitCost: cost,
      unitCostCurrency: cost != null ? unitCostCurrency : undefined,
      supplier: supplier.trim() || undefined,
      condition,
      notes: notes.trim() || undefined,
      status,
      createdAt: asset?.createdAt ?? now,
      updatedAt: now,
    });
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-3 sm:p-6"
      data-soft-keyboard-handled
    >
      <div
        className="flex w-full max-w-4xl max-h-[92vh] flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {asset ? 'Edit asset' : 'Add asset'}
            </h2>
            <p className="mt-0.5 text-sm text-gray-500">
              Reusable kit — not food inventory.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-2xl leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="mb-5">
              <span className="mb-2 block text-sm font-medium text-gray-700">Type</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleKindChange('quantity')}
                  className={`flex-1 rounded-md border py-2.5 px-3 text-sm font-medium ${
                    kind === 'quantity'
                      ? 'border-pink-500 bg-pink-500 text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Quantity line
                </button>
                <button
                  type="button"
                  onClick={() => handleKindChange('tagged')}
                  className={`flex-1 rounded-md border py-2.5 px-3 text-sm font-medium ${
                    kind === 'tagged'
                      ? 'border-pink-500 bg-pink-500 text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Tagged item
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {kind === 'quantity'
                  ? 'One row for a type you count (plates × 200, jackets × 12).'
                  : 'One physical piece with an optional tag or serial.'}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label htmlFor="asset-name" className="mb-1 block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  id="asset-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={blockEnterSubmit}
                  data-soft-keyboard-handled
                  className={inputClass}
                  placeholder="e.g. Dinner plates, chafing dishes, chef jackets"
                  required
                />
              </div>

              <div>
                <label htmlFor="asset-category" className="mb-1 block text-sm font-medium text-gray-700">
                  Category
                </label>
                <select
                  id="asset-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as AssetCategory)}
                  className={inputClass}
                >
                  {ASSET_CATEGORIES.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="asset-location" className="mb-1 block text-sm font-medium text-gray-700">
                  Location
                </label>
                <select
                  id="asset-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value as AssetLocation)}
                  className={inputClass}
                >
                  {ASSET_LOCATIONS.map((l) => (
                    <option key={l.id} value={l.id}>{l.label}</option>
                  ))}
                </select>
              </div>

              {kind === 'quantity' ? (
                <>
                  <div>
                    <label htmlFor="asset-qty" className="mb-1 block text-sm font-medium text-gray-700">
                      Quantity
                    </label>
                    <input
                      id="asset-qty"
                      type="number"
                      min={0}
                      step={1}
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      onKeyDown={blockEnterSubmit}
                      data-soft-keyboard-handled
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="asset-replace" className="mb-1 block text-sm font-medium text-gray-700">
                      Replace below
                    </label>
                    <input
                      id="asset-replace"
                      type="number"
                      min={0}
                      step={1}
                      value={replaceBelow}
                      onChange={(e) => setReplaceBelow(e.target.value)}
                      onKeyDown={blockEnterSubmit}
                      data-soft-keyboard-handled
                      className={inputClass}
                      placeholder="optional"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label htmlFor="asset-tag" className="mb-1 block text-sm font-medium text-gray-700">
                      Asset tag
                    </label>
                    <input
                      id="asset-tag"
                      type="text"
                      value={assetTag}
                      onChange={(e) => setAssetTag(e.target.value)}
                      onKeyDown={blockEnterSubmit}
                      data-soft-keyboard-handled
                      className={inputClass}
                      placeholder="optional"
                    />
                  </div>
                  <div>
                    <label htmlFor="asset-serial" className="mb-1 block text-sm font-medium text-gray-700">
                      Serial
                    </label>
                    <input
                      id="asset-serial"
                      type="text"
                      value={serialNumber}
                      onChange={(e) => setSerialNumber(e.target.value)}
                      onKeyDown={blockEnterSubmit}
                      data-soft-keyboard-handled
                      className={inputClass}
                      placeholder="optional"
                    />
                  </div>
                </>
              )}

              <div>
                <label htmlFor="asset-status" className="mb-1 block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  id="asset-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as AssetStatus)}
                  className={inputClass}
                >
                  {ASSET_STATUSES.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="asset-condition" className="mb-1 block text-sm font-medium text-gray-700">
                  Condition
                </label>
                <select
                  id="asset-condition"
                  value={condition}
                  onChange={(e) => setCondition(e.target.value as AssetCondition)}
                  className={inputClass}
                >
                  {ASSET_CONDITIONS.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <span className="mb-1 block text-sm font-medium text-gray-700">
                  Unit cost
                </span>
                <div className="flex max-w-lg items-stretch gap-3">
                  <div className="w-28 shrink-0">
                    <label htmlFor="asset-currency" className="sr-only">Currency</label>
                    <select
                      id="asset-currency"
                      value={unitCostCurrency}
                      onChange={(e) => setUnitCostCurrency(e.target.value as AssetCurrency)}
                      className={inputClass}
                    >
                      {ASSET_CURRENCIES.map((c) => (
                        <option key={c.id} value={c.id}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-0 flex-1">
                    <label htmlFor="asset-cost" className="sr-only">Amount</label>
                    <input
                      id="asset-cost"
                      type="number"
                      min={0}
                      step="0.01"
                      value={unitCost}
                      onChange={(e) => setUnitCost(e.target.value)}
                      onKeyDown={blockEnterSubmit}
                      data-soft-keyboard-handled
                      className={inputClass}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="asset-purchased" className="mb-1 block text-sm font-medium text-gray-700">
                  Purchase date
                </label>
                <input
                  id="asset-purchased"
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="asset-supplier" className="mb-1 block text-sm font-medium text-gray-700">
                  Supplier
                </label>
                <input
                  id="asset-supplier"
                  type="text"
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  onKeyDown={blockEnterSubmit}
                  data-soft-keyboard-handled
                  className={inputClass}
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="asset-notes" className="mb-1 block text-sm font-medium text-gray-700">
                  Notes
                </label>
                <textarea
                  id="asset-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-soft-keyboard-handled
                  rows={3}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-shrink-0 justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-white px-5 py-2 font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-lg bg-pink-500 px-5 py-2 font-bold text-white hover:bg-pink-600"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssetModal;
